// packages/core/src/schedule/intervals.ts
//
// Arithmétique pure sur les plages horaires d'un jour : alignement sur le
// quart d'heure (D8), refus de chevauchement (D3), bornage par les voisines
// pendant un geste (D12), durée minimale (D4). Aucune plage retournée par ce
// module ne viole jamais ces règles — un appelant n'a pas besoin de revalider.
//
// Toutes les fonctions prennent et rendent des minutes depuis minuit
// (0..DAY_MINUTES), jamais des heures/minutes séparées : la conversion vers
// {hour, minute} n'a lieu qu'aux frontières (storage.ts, composants).

import {
  DAY_MINUTES,
  MIN_DURATION_MINUTES,
  type Range,
  type RangeOpResult,
} from "./types";

/** Arrondit à la grille de `step` minutes la plus proche EN DESSOUS. */
export function floorToStep(minutes: number, step: number): number {
  return Math.floor(minutes / step) * step;
}

/** Arrondit à la grille de `step` minutes la plus proche AU-DESSUS. */
export function ceilToStep(minutes: number, step: number): number {
  return Math.ceil(minutes / step) * step;
}

/** Arrondit à la grille de 15 min la plus proche EN DESSOUS (D8 : plancher absolu, indépendant du pas affiché). */
export function floorTo15(minutes: number): number {
  return floorToStep(minutes, 15);
}

/** Arrondit à la grille de 15 min la plus proche AU-DESSUS (D8 : plancher absolu, indépendant du pas affiché). */
export function ceilTo15(minutes: number): number {
  return ceilToStep(minutes, 15);
}

/** Une plage est valide en soi (bornes, alignement, durée) — pas de chevauchement ici. */
export function isValidRange(range: Range): RangeOpResult {
  if (range.end <= range.start) {
    return { ok: false, reason: "inverted" };
  }
  if (range.start < 0 || range.end > DAY_MINUTES) {
    return { ok: false, reason: "out-of-bounds" };
  }
  if (range.start % 15 !== 0 || range.end % 15 !== 0) {
    return { ok: false, reason: "misaligned" };
  }
  if (range.end - range.start < MIN_DURATION_MINUTES) {
    return { ok: false, reason: "too-short" };
  }
  return { ok: true, range };
}

/** Deux plages [a.start,a.end) et [b.start,b.end) se chevauchent-elles (bornes exclues) ? */
export function rangesOverlap(a: Range, b: Range): boolean {
  return a.start < b.end && b.start < a.end;
}

/** La plage chevauche-t-elle au moins une des `existing` (hors `excludeIndex`, pour un déplacement) ? */
export function overlapsAny(
  range: Range,
  existing: Range[],
  excludeIndex?: number,
): boolean {
  return existing.some((r, i) => i !== excludeIndex && rangesOverlap(range, r));
}

export function sortRanges(ranges: Range[]): Range[] {
  return [...ranges].sort((a, b) => a.start - b.start);
}

/**
 * Borne un geste directionnel qui part de `anchorMinute` (point où le doigt
 * ou le clic a commencé, FIXE) et grandit vers `towardMinute` (D12) : le
 * résultat s'arrête au premier obstacle rencontré en chemin plutôt que de
 * sauter par-dessus. Exemple de la spec : glisser de 08:00 vers 11:00 avec
 * une plage 09:00-10:00 déjà là → tronqué à 08:00-09:00 (pas de morceau
 * 10:00-11:00 de l'autre côté : un SEUL geste = une SEULE plage, D1).
 *
 * Aligne ensuite sur le pas affiché (`step`, 15 min par défaut — D8 reste le
 * plancher absolu, `isValidRange` le revérifie) et vérifie la durée minimale
 * (D4). `excludeIndex` : la plage qu'on étend/déplace ne se borne pas
 * elle-même.
 */
export function boundBySiblings(
  anchorMinute: number,
  towardMinute: number,
  existing: Range[],
  excludeIndex?: number,
  step: number = 15,
): RangeOpResult {
  const anchor = Math.max(0, Math.min(DAY_MINUTES, anchorMinute));
  const toward = Math.max(0, Math.min(DAY_MINUTES, towardMinute));
  const others = existing.filter((_, i) => i !== excludeIndex);

  let start: number;
  let end: number;
  if (toward >= anchor) {
    // Croît vers l'avant depuis l'ancre : chaque voisine qui commence entre
    // l'ancre et la fin courante rapproche la fin (premier obstacle gagne).
    start = anchor;
    end = toward;
    for (const r of others) {
      if (r.start >= start && r.start < end) {
        end = r.start;
      }
    }
  } else {
    // Croît vers l'arrière depuis l'ancre : chaque voisine qui finit entre
    // le début courant et l'ancre repousse le début (premier obstacle gagne).
    start = toward;
    end = anchor;
    for (const r of others) {
      if (r.end <= end && r.end > start) {
        start = r.end;
      }
    }
  }

  const range: Range = { start: ceilToStep(start, step), end: floorToStep(end, step) };
  const validity = isValidRange(range);
  if (!validity.ok) return validity;
  // Filet : l'arithmétique ci-dessus ne devrait plus jamais chevaucher,
  // l'alignement au pas pourrait en théorie ré-empiéter d'un cran.
  if (overlapsAny(range, existing, excludeIndex)) {
    return { ok: false, reason: "overlap" };
  }
  return { ok: true, range };
}

/**
 * Ajoute une plage [start, end) telle quelle si elle est valide et ne
 * chevauche rien (D3 : chevauchement refusé, pas de fusion). Ne fait AUCUN
 * bornage — c'est le rôle de `boundBySiblings` pendant le geste. Utilisé une
 * fois le geste terminé, avec les valeurs déjà bornées.
 */
export function tryAddRange(existing: Range[], range: Range): RangeOpResult {
  const validity = isValidRange(range);
  if (!validity.ok) return validity;
  if (overlapsAny(range, existing)) {
    return { ok: false, reason: "overlap" };
  }
  return { ok: true, range };
}

/** Retire la plage à l'index donné. */
export function removeRangeAt(ranges: Range[], index: number): Range[] {
  return ranges.filter((_, i) => i !== index);
}

/**
 * Déplace la plage `index` de `deltaMinutes` (peut être négatif) EN BLOC,
 * sans bornage progressif : refuse tout net en cas de chevauchement (D14 :
 * "accroche au pas, refus si chevauchement" — contrairement au geste de
 * peinture D12, un déplacement ne se tronque pas). Utilisé par le
 * déplacement à la souris et par les flèches haut/bas au clavier (D17, un
 * pas à la fois — l'appelant passe `deltaMinutes = ±step`).
 */
export function moveRangeBy(
  ranges: Range[],
  index: number,
  deltaMinutes: number,
): RangeOpResult {
  const target = ranges[index];
  if (!target) return { ok: false, reason: "out-of-bounds" };
  const duration = target.end - target.start;
  const rawStart = target.start + deltaMinutes;
  const rawEnd = rawStart + duration;
  if (rawStart < 0 || rawEnd > DAY_MINUTES) {
    return { ok: false, reason: "out-of-bounds" };
  }
  const range: Range = { start: rawStart, end: rawEnd };
  const validity = isValidRange(range);
  if (!validity.ok) return validity;
  if (overlapsAny(range, ranges, index)) {
    return { ok: false, reason: "overlap" };
  }
  return { ok: true, range };
}

/**
 * Étend la plage `index` jusqu'à `targetMinute` (D15 : Maj + clic ; D25 :
 * second tap mobile). Le bord OPPOSÉ à `targetMinute` reste fixe (ancre) ;
 * le geste est ensuite borné comme un glisser normal (D12).
 */
export function extendRangeTo(
  ranges: Range[],
  index: number,
  targetMinute: number,
  step: number = 15,
): RangeOpResult {
  const target = ranges[index];
  if (!target) return { ok: false, reason: "out-of-bounds" };
  if (targetMinute < target.start) {
    return boundBySiblings(target.end, targetMinute, ranges, index, step);
  }
  if (targetMinute > target.end) {
    return boundBySiblings(target.start, targetMinute, ranges, index, step);
  }
  // Cible à l'intérieur de la plage : rien à étendre.
  return { ok: true, range: target };
}

/** Deux plages sont-elles contiguës (fin de l'une = début de l'autre) ? Pour D1b « Fusionner ». */
export function isAdjacent(a: Range, b: Range): boolean {
  return a.end === b.start || b.end === a.start;
}

/** Résultat de `mergeWithAdjacent` : porte `neighborIndex` en plus de `range`,
 * pour que l'appelant retire EXACTEMENT la voisine fusionnée — jamais toute
 * plage touchant un bord de `target`, qui supprimerait une 3ᵉ plage
 * contiguë de l'AUTRE côté sans la fusionner (bug vécu : trois plages
 * bord-à-bord, fusionner la plage du milieu effaçait la 3ᵉ au lieu de la
 * laisser telle quelle). */
export type MergeResult =
  | { ok: true; range: Range; neighborIndex: number }
  | {
      ok: false;
      reason: "overlap" | "too-short" | "out-of-bounds" | "inverted" | "misaligned";
    };

/** Fusionne la plage `index` avec sa voisine contiguë, si elle existe (D1b). */
export function mergeWithAdjacent(ranges: Range[], index: number): MergeResult {
  const target = ranges[index];
  if (!target) return { ok: false, reason: "out-of-bounds" };
  const neighborIndex = ranges.findIndex(
    (r, i) => i !== index && isAdjacent(r, target),
  );
  if (neighborIndex === -1) {
    return { ok: false, reason: "out-of-bounds" };
  }
  const neighbor = ranges[neighborIndex];
  const merged: Range = {
    start: Math.min(target.start, neighbor.start),
    end: Math.max(target.end, neighbor.end),
  };
  const validity = isValidRange(merged);
  if ("reason" in validity) return validity;
  return { ok: true, range: validity.range, neighborIndex };
}

/**
 * Découpe la plage `index` en deux à `atMinute` (D1b « Découper »). `atMinute`
 * doit tomber strictement à l'intérieur, aligné sur 15 min.
 */
export function splitRangeAt(
  ranges: Range[],
  index: number,
  atMinute: number,
): { ok: true; first: Range; second: Range } | { ok: false; reason: string } {
  const target = ranges[index];
  if (!target) return { ok: false, reason: "out-of-bounds" };
  const cut = floorTo15(atMinute);
  if (cut <= target.start || cut >= target.end) {
    return { ok: false, reason: "invalid-cut" };
  }
  return {
    ok: true,
    first: { start: target.start, end: cut },
    second: { start: cut, end: target.end },
  };
}
